import React from 'react'
import styles from './SettingsMenu.scss'
import { Icon } from 'common/Icon'
import { PopoverMenu, PopoverMenuItem, PopoverMenuHeading } from 'common/styleguide/PopoverMenu'
import { RadioGroup } from 'common/styleguide/RadioGroup'
import { CheckboxGroup } from 'common/styleguide/CheckboxGroup'
import { Msg, fm, featureNames, commonNouns } from 'lib/localization'
import cx from 'classnames'
import { OrderedMap } from 'immutable'
import { defineMessages } from 'react-intl'

export const settingsMessages = defineMessages({
  autoCenter: {
    id: 'SettingsMenu.autoCenter',
    defaultMessage: 'Auto Center'
  },
  bikePaths: {
    id: 'SettingsMenu.bikePaths',
    defaultMessage: 'Bike Paths'
  },
  cueIcons: {
    id: 'SettingsMenu.cueIcons',
    defaultMessage: 'Cue Icons'
  },
  distanceMarkers: {
    id: 'SettingsMenu.distanceMarkers',
    defaultMessage: 'Distance Markers'
  },
  surfaces: {
    id: 'SettingsMenu.surfaces',
    defaultMessage: 'Surfaces'
  },
  generateCues: {
    id: 'SettingsMenu.generateCues',
    defaultMessage: 'Generate Cuesheet'
  },
  mapOverlays: {
    id: 'SettingsMenu.mapOverlays',
    defaultMessage: 'Map Overlays'
  },
  zoomByScrolling: {
    id: 'SettingsMenu.zoomByScrolling',
    defaultMessage: 'Zoom by Scrolling'
  },
  zoomToSelections: {
    id: 'SettingsMenu.zoomToSelections',
    defaultMessage: 'Zoom To Selections'
  }
})

const overlays = intl => OrderedMap({
  showHeatLayer: fm(intl, featureNames.heatmap),
  showTrackSurface: fm(intl, settingsMessages.surfaces),
  showPOIs: fm(intl, featureNames.pointsOfInterest, { count: 0 }),
  showBikePaths: fm(intl, settingsMessages.bikePaths),
  showDistanceMarkers: fm(intl, settingsMessages.distanceMarkers)
})

const plannerOverlays = intl => OrderedMap({
  showTrackSurface: fm(intl, settingsMessages.surfaces),
  showCueIcons: fm(intl, settingsMessages.cueIcons),
  showPOIs: fm(intl, featureNames.pointsOfInterest, { count: 1 }),
  showDistanceMarkers: fm(intl, settingsMessages.distanceMarkers)
})

const options = intl => OrderedMap({
  scrollWheelZoom: fm(intl, settingsMessages.zoomByScrolling),
  autoZoom: fm(intl, settingsMessages.zoomToSelections)
})

const plannerOptions = intl => OrderedMap({
  autoCenter: fm(intl, settingsMessages.autoCenter),
  scrollWheelZoom: fm(intl, settingsMessages.zoomByScrolling),
  generateCues: fm(intl, settingsMessages.generateCues)
})

export class SettingsMenu extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      isMounted: false,
      menuOpen: false
    }
  }

  componentDidMount () {
    this.setState({ isMounted: true })
  }

  toggle = () => {
    this.setState({ menuOpen: !this.state.menuOpen })
  }

  onToggleOverlay = (overlayKey) => {
    const { mapSettings } = this.props
    if (overlayKey === 'showDistanceMarkers') {
      (this.props.onDistanceMarkersChange ?? this.props.onShowDistanceMarkersChange)(!mapSettings.showDistanceMarkers)
    }
    if (overlayKey === 'showPOIs') {
      (this.props.onPOIsChange ?? this.props.onShowPOIsChange)(!mapSettings.showPOIs)
    }
    if (overlayKey === 'showCueIcons') {
      this.props.onShowCueIconsChange(!mapSettings.showCueIcons)
    }
    if (overlayKey === 'showHeatLayer') {
      this.props.onShowHeatLayerChange(!mapSettings.showHeatLayer)
    }
    if (overlayKey === 'showBikePaths') {
      this.props.onShowBikePathsChange(!mapSettings.showBikePaths)
    }
    if (overlayKey === 'showTrackSurface') {
      this.props.onShowTrackSurfaceChange(!mapSettings.showTrackSurface)
    }
  }

  onOptionsChange = (optionKey) => {
    const { mapSettings } = this.props
    if (optionKey === 'scrollWheelZoom') {
      this.props.onScrollWheelZoomChange(!mapSettings.scrollWheelZoom)
    }
    if (optionKey === 'autoZoom') {
      this.props.onAutoZoomChange(!mapSettings.autoZoom)
    }
    if (optionKey === 'autoCenter') {
      this.props.onAutoCenterChange(!mapSettings.autoCenter)
    }
    if (optionKey === 'generateCues') {
      this.props.onGenerateCuesChange(!mapSettings.generateCues)
    }
  }

  autoHide = () => {
    this._hideTimeout = setTimeout(() => {
      this.setState({ menuOpen: false })
    }, 1500)
  }

  dontAutoHide = () => {
    clearTimeout(this._hideTimeout)
  }

  render () {
    const { isMounted, menuOpen } = this.state
    const { mapSettings, overlayType, unitsOptions, onUnitsChange, intl, trouteHasSurface, planner } = this.props

    const _allOverlays = planner ? plannerOverlays(intl) : overlays(intl)
    let allOverlays
    if (planner) {
      allOverlays = window.Routes?.activeMap?.activeRoute.showSurface
        ? _allOverlays
        : _allOverlays.delete('showTrackSurface')
    } else {
      allOverlays = trouteHasSurface
        ? _allOverlays
        : _allOverlays.delete('showTrackSurface')
    }
    const maybeBikePaths = (overlayType === 'google' && !planner)
      ? allOverlays
      : allOverlays.remove('showBikePaths')
    const activeOverlays = maybeBikePaths.keySeq().filter(k => mapSettings[k]).toSet()
    const allOptions = planner ? plannerOptions(intl) : options(intl)
    const activeOptions = allOptions.keySeq().filter(k => mapSettings[k]).toSet()

    return (
      <div className={styles.SettingsMenu}>
        <button ref={r => { this._button = r }} onClick={this.toggle} className={cx(menuOpen && styles.active)}>
          <Icon i='ios-settings' /> <Msg m={featureNames.settings} />
        </button>
        {isMounted && menuOpen && <PopoverMenu
          intl={intl}
          target={this._button}
          container={window.body}
          placement='bottom'
          onRequestClose={() => this.setState({ menuOpen: false })} >
          <div onMouseLeave={this.autoHide} onMouseEnter={this.dontAutoHide}>
            <PopoverMenuHeading>
              <Msg m={settingsMessages.mapOverlays} />
            </PopoverMenuHeading>
            <PopoverMenuItem>
              <CheckboxGroup
                style={{ margin: '0 0' }}
                horizontal
                options={maybeBikePaths}
                selected={activeOverlays}
                onToggleItem={this.onToggleOverlay} />
            </PopoverMenuItem>
            <PopoverMenuHeading><Msg m={commonNouns.options} /></PopoverMenuHeading>
            <PopoverMenuItem>
              <CheckboxGroup
                style={{ margin: '0 0' }}
                horizontal
                options={allOptions}
                selected={activeOptions}
                onToggleItem={this.onOptionsChange} />
            </PopoverMenuItem>
            <PopoverMenuHeading><Msg m={commonNouns.units} /></PopoverMenuHeading>
            <PopoverMenuItem>
              <RadioGroup
                horizontal
                style={{ margin: '0 0' }}
                options={unitsOptions}
                selected={mapSettings.metricUnits ? 'metric' : 'imperial'}
                onChange={onUnitsChange} />
            </PopoverMenuItem>
          </div>
        </PopoverMenu>}
      </div>
    )
  }
}
